import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import NotesIcon from '@mui/icons-material/Notes';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BadgeIcon from '@mui/icons-material/Badge';
import CakeIcon from '@mui/icons-material/Cake';
import { apiService } from '../../../services/api';
import Navigation from '../../../components/portal/Navigation';
import UnifiedModal from '../../../components/portal/common/UnifiedModal';

import { useTranslation } from 'react-i18next';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1000px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);
  padding-top: 40px;

  @media (max-width: 768px) {
    padding: 1rem var(--mobile-page-padding-x);
    padding-top: 32px;
  }
`;

const ContentCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  border: 1px solid var(--card-border, rgba(0, 0, 0, 0.05));
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;

  .main-info {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .icon-box {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--primary-orange), var(--primary-blue));
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    svg { font-size: 1.75rem; }
  }

  .title-area {
    h2 { margin: 0; font-size: 1.5rem; font-weight: 900; color: #222; }
    p { margin: 0.15rem 0 0; color: #666; font-weight: 500; display: flex; align-items: center; gap: 0.4rem; font-size: 0.9rem; }
  }
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 800;
  color: #222;
  margin: 1.5rem 0 1rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
  
  &:first-of-type {
    margin-top: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.25rem;
  margin-bottom: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  .label {
    font-size: 0.75rem;
    font-weight: 800;
    color: #888;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .value {
    font-size: 1.05rem;
    font-weight: 700;
    color: #222;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.4rem 1rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 800;
  color: #fff;
  text-transform: uppercase;
  background: ${p => {
    switch (p.status) {
      case 'PASSED': return 'linear-gradient(135deg, #11998e, #38ef7d)';
      case 'FAILED': return 'linear-gradient(135deg, #fc4a1a, #f7b733)';
      case 'UPCOMING': return 'linear-gradient(135deg, #667eea, #764ba2)';
      default: return 'linear-gradient(135deg, #6b7280, #9ca3af)';
    }
  }};
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const NoteContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 1rem;

  .note-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
    font-size: 0.85rem;
    .author { font-weight: 800; color: #444; }
    .date { color: #888; }
  }

  .note-content {
    font-size: 0.95rem;
    color: #333;
    line-height: 1.5;
    white-space: pre-wrap;
  }
`;

const ActionButton = styled(motion.button)<{ variant?: 'primary' | 'secondary' | 'success' | 'danger' }>`
  border: none;
  border-radius: 12px;
  padding: 0.6rem 1.2rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #fff;
  background: ${p => {
    if (p.variant === 'success') return 'linear-gradient(135deg, #11998e, #38ef7d)';
    if (p.variant === 'danger') return 'linear-gradient(135deg, #fc4a1a, #f7b733)';
    if (p.variant === 'secondary') return '#f1f3f4';
    return 'linear-gradient(135deg, #667eea, #764ba2)';
  }};
  ${p => p.variant === 'secondary' && 'color: #444;'}
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Toast = styled(motion.div)<{ variant?: 'error' | 'success' }>`
  position: fixed;
  top: 100px;
  right: 24px;
  z-index: 5000;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  background: ${p => p.variant === 'error' ? 'linear-gradient(135deg, #fc4a1a, #f7b733)' : 'linear-gradient(135deg, #11998e, #38ef7d)'};
`;

const TeacherExamDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { examId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string|null>(null);
  const [toast, setToast] = React.useState<{msg: string, variant: 'success' | 'error'}|null>(null);
  const [exam, setExam] = React.useState<any|null>(null);
  const [teacherNote, setTeacherNote] = React.useState<string>('');
  const [studentNote, setStudentNote] = React.useState<string>('');
  const [savingNote, setSavingNote] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  const [students, setStudents] = React.useState<{id: string, name: string}[]>([]);

  const [editData, setEditData] = React.useState({
    startTime: '',
    location: '',
    tips: '',
    type: '',
    licenseType: '',
    studentId: '',
    price: ''
  });

  const load = React.useCallback(async ()=>{
    if (!examId) return;
    setLoading(true); setError(null);
    try {
      const [exResp, stResp] = await Promise.all([
        apiService.get<any>(`/api/exams/teacher/${examId}`),
        apiService.get<any>('/api/teacher/students')
      ]);
      const data = (exResp && typeof exResp === 'object' && 'data' in exResp) ? (exResp as any).data : exResp;
      setExam(data || null);

      if (data) {
        setEditData({
          startTime: data.startTime ? data.startTime.slice(0, 16) : '',
          location: data.location || data.examPlace || '',
          tips: data.tips || '',
          type: data.type || '',
          licenseType: data.licenseType || '',
          studentId: data.studentId ? String(data.studentId) : (data.student?.id ? String(data.student.id) : ''),
          price: data.price ? String(data.price) : ''
        });

        // Resolve notes
        if (Array.isArray(data.notes)) {
          const tNotes = data.notes.filter((n: any) => n.author?.roles?.some((r: any) => r.name === 'ROLE_TEACHER' || r.name === 'ROLE_SUPER_ADMIN'));
          const sNotes = data.notes.filter((n: any) => n.author?.roles?.some((r: any) => r.name === 'ROLE_STUDENT'));
          const tNote = tNotes.length > 0 ? tNotes[tNotes.length - 1] : null;
          const sNote = sNotes.length > 0 ? sNotes[sNotes.length - 1] : null;
          setTeacherNote(tNote?.content || '');
          setStudentNote(sNote?.content || '');
        }
      }

      const studs = Array.isArray((stResp as any)?.data) ? (stResp as any).data : (Array.isArray(stResp) ? stResp : []);
      setStudents(studs.map((s: any) => ({
        id: String(s.id),
        name: `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.username || s.email || `Student ${s.id}`
      })));
    } catch(e:any){ setError(e?.message || (t('portal.exams.error_load', 'Failed to load exams') as string)); }
    finally{ setLoading(false); }
  }, [examId, t]);

  React.useEffect(()=>{ load(); }, [load]);

  const showToast = (msg: string, variant: 'success' | 'error' = 'success') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 3000);
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      await apiService.patch(`/api/exams/teacher/${examId}`, {
        ...editData,
        price: editData.price ? parseFloat(editData.price) : null,
        studentId: (editData.studentId && editData.studentId !== '') ? parseInt(editData.studentId) : 0
      });
      showToast(t('portal.exams.updated_success', 'Exam updated successfully') as string);
      setShowEdit(false);
      await load();
    } catch (e: any) {
      showToast(e?.message || (t('portal.exams.update_error', 'Failed to update exam') as string), 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveTeacherNote = async () => {
    const content = teacherNote.trim();
    if (!content) { showToast(t('portal.exams.note_empty', 'Note cannot be empty') as string, 'error'); return; }
    try {
      setSavingNote(true);
      const qs = new URLSearchParams();
      qs.set('content', content);
      await apiService.post<any>(`/api/teacher/exams/${examId}/notes?${qs.toString()}`);
      showToast(t('portal.exams.note_success', 'Note saved') as string);
      await load();
    } catch(e:any){ showToast(e?.message || (t('portal.exams.note_error', 'Failed to save note') as string), 'error'); }
    finally{ setSavingNote(false); }
  };

  const formatDate = (iso?: string) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : (i18n.language === 'tr' ? 'tr-TR' : 'en-GB'),{ year:'numeric', month:'long', day:'2-digit', timeZone:'Europe/Amsterdam' }); }
    catch { return new Date(iso).toLocaleDateString(i18n.language === 'nl' ? 'nl-NL' : (i18n.language === 'tr' ? 'tr-TR' : 'en-GB'),{ year:'numeric', month:'long', day:'2-digit' }); }
  };
  
  const formatDateTime = (iso?: string) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(i18n.language === 'nl' ? 'nl-NL' : (i18n.language === 'tr' ? 'tr-TR' : 'en-GB'),{ dateStyle:'medium', timeStyle:'short', timeZone:'Europe/Amsterdam' }); }
    catch { return new Date(iso).toLocaleString(i18n.language === 'nl' ? 'nl-NL' : (i18n.language === 'tr' ? 'tr-TR' : 'en-GB'),{ dateStyle:'medium', timeStyle:'short' }); }
  };

  const computeStatus = (e: any): 'UPCOMING'|'PASSED'|'FAILED'|'COMPLETED' => {
    if (e?.status) return e.status;
    try {
      const now = new Date();
      const dateStr = e?.startTime || e?.examDate;
      const dt = dateStr ? new Date(dateStr) : null;
      
      if (dt) {
        // If the exam is in the future, it's definitely UPCOMING
        if (dt > now) return 'UPCOMING';
        
        // "the result can only be edited by teacher one hour after the xame date and time."
        // So until 1 hour after the start time, if result is not set (is false), we still call it UPCOMING or similar.
        const oneHourAfter = new Date(dt.getTime() + 60 * 60 * 1000);
        if (now < oneHourAfter && typeof e?.result === 'boolean' && !e.result) {
          return 'UPCOMING';
        }
      }
      
      if (typeof e?.result === 'boolean') return e.result ? 'PASSED' : 'FAILED';
      return 'COMPLETED';
    } catch {
      return 'COMPLETED';
    }
  };

  const canEditResult = React.useMemo(() => {
    if (!exam) return false;
    const now = new Date();
    const dateStr = exam.startTime || exam.examDate;
    if (!dateStr) return false;
    const dt = new Date(dateStr);
    const oneHourAfter = new Date(dt.getTime() + 60 * 60 * 1000);
    return now >= oneHourAfter;
  }, [exam]);

  const updateResult = async (res: boolean) => {
    try {
      setSaving(true);
      await apiService.patch(`/api/teacher/exams/${examId}/result`, { result: res });
      showToast(t('portal.exams.result_updated', 'Result updated successfully') as string);
      await load();
    } catch (e: any) {
      showToast(e?.message || (t('portal.exams.result_error', 'Failed to update result') as string), 'error');
    } finally {
      setSaving(false);
    }
  };

  const studentLabel = () => {
    const sid = exam?.studentId || exam?.student?.id;
    if (!sid) return '';
    const found = students.find(s => String(s.id) === String(sid));
    if (found) return found.name;
    if (exam.student) {
      const s = exam.student;
      const name = `${s.firstName || ''} ${s.lastName || ''}`.trim();
      return name || s.username || s.email || `Student ${s.id}`;
    }
    return `Student ${sid}`;
  };

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        <AnimatePresence>
          {toast && (
            <Toast 
              variant={toast.variant}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
            >
              {toast.variant === 'success' ? '✅' : '⚠️'} {toast.msg}
            </Toast>
          )}
        </AnimatePresence>

        <ContentCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} style={{ display: 'inline-block' }}>
                <ScheduleIcon fontSize="large" style={{ color: 'var(--primary-blue)' }} />
              </motion.div>
              <p style={{ marginTop: '1rem', color: '#666' }}>{t('portal.exams.loading_details', 'Loading exam details…')}</p>
            </div>
          ) : !exam ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <CancelIcon sx={{ fontSize: 48, opacity: 0.2 }} />
              <p style={{ marginTop: '1rem' }}>{error || t('portal.exams.not_found', 'Exam not found or not authorized.')}</p>
            </div>
          ) : (
            <>
              <Header>
                <div className="main-info">
                  <div className="icon-box">
                    <EventIcon />
                  </div>
                  <div className="title-area">
                    <h2>{t('portal.exams.detail_title', 'Exam Details')} #{examId}</h2>
                    <p><LocationOnIcon sx={{ fontSize: 16 }} /> {exam.location || t('portal.exams.no_location', 'No location set')}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <ActionButton onClick={() => setShowEdit(true)} style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
                    {t('portal.common.edit', 'Edit')}
                  </ActionButton>
                  <StatusBadge status={computeStatus(exam)}>{t(`portal.exams.status.${computeStatus(exam).toLowerCase()}`, computeStatus(exam))}</StatusBadge>
                </div>
              </Header>

              <SectionTitle><PersonIcon /> {t('portal.exams.detail_title', 'Exam Details')}</SectionTitle>
              <DetailGrid>
                <DetailItem>
                  <div className="label"><ScheduleIcon sx={{ fontSize: 14 }} /> {t('portal.exams.date_time', 'Date & Time')}</div>
                  <div className="value">{formatDateTime(exam.startTime)}</div>
                </DetailItem>
                <DetailItem>
                  <div className="label"><PersonIcon sx={{ fontSize: 14 }} /> {t('portal.register.student', 'Student')}</div>
                  <div className="value">{studentLabel()}</div>
                </DetailItem>
                <DetailItem>
                  <div className="label"><CheckCircleIcon sx={{ fontSize: 14 }} /> {t('portal.exams.result', 'Result')}</div>
                  <div className="value" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {(() => {
                      const now = new Date();
                      const examTime = exam.startTime;
                      const dt = examTime ? new Date(examTime) : null;
                      const isFuture = dt && dt > now;
                      
                      if (isFuture) {
                        return <span style={{ fontWeight: 800, color: '#666' }}>—</span>;
                      }

                      return (
                        <span style={{ fontWeight: 800, color: typeof exam.result === 'boolean' ? (exam.result ? '#11998e' : '#fc4a1a') : '#666' }}>
                          {typeof exam.result === 'boolean' ? (exam.result ? t('portal.exams.passed', 'Passed') : t('portal.exams.failed', 'Failed')) : t('portal.exams.pending', 'Pending')}
                        </span>
                      );
                    })()}
                    {canEditResult && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <ActionButton 
                          variant="success" 
                          onClick={() => updateResult(true)} 
                          disabled={saving || exam.result === true}
                          style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                        >
                          {t('portal.exams.set_passed', 'Set Passed')}
                        </ActionButton>
                        <ActionButton 
                          variant="danger" 
                          onClick={() => updateResult(false)} 
                          disabled={saving || exam.result === false}
                          style={{ padding: '0.2rem 0.6rem', fontSize: '0.75rem' }}
                        >
                          {t('portal.exams.set_failed', 'Set Failed')}
                        </ActionButton>
                      </div>
                    )}
                  </div>
                  {!canEditResult && (
                    <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.25rem' }}>
                      {t('portal.exams.edit_later', 'Result can be edited 1 hour after exam start')}
                    </div>
                  )}
                </DetailItem>
                <DetailItem>
                  <div className="label"><BadgeIcon sx={{ fontSize: 14 }} /> {t('portal.exams.detail_title', 'Exam Details')} ID</div>
                  <div className="value">#{examId}</div>
                </DetailItem>
              </DetailGrid>

              <SectionTitle><NotesIcon /> {t('portal.exams.notes_title', 'Examiner Notes')}</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                <div>
                  <div className="label" style={{ fontSize: '0.75rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    {t('portal.exams.teacher_note', 'Teacher Note')}
                  </div>
                  <textarea
                    value={teacherNote}
                    onChange={(e) => setTeacherNote(e.target.value.slice(0, 2000))}
                    placeholder={t('portal.exams.teacher_note_placeholder', 'Enter teacher note...') as string}
                    disabled={savingNote}
                    style={{ width: '100%', minHeight: 120, padding: '1rem', borderRadius: 12, border: '2px solid rgba(0,0,0,0.05)', background: 'white', fontSize: '0.95rem', outline: 'none', transition: 'border-color 0.2s', resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                    <div style={{ fontSize: 12, color: '#888' }}>{teacherNote.length} / 2000</div>
                    <ActionButton
                      variant="success"
                      onClick={saveTeacherNote}
                      disabled={savingNote || !teacherNote.trim()}
                      style={{ padding: '0.4rem 1rem', fontSize: '0.85rem' }}
                    >
                      {savingNote ? t('portal.common.saving', 'Saving...') : t('portal.common.save', 'Save')}
                    </ActionButton>
                  </div>
                </div>

                <div>
                  <div className="label" style={{ fontSize: '0.75rem', fontWeight: 800, color: '#888', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    {t('portal.exams.student_note', 'Student Note')}
                  </div>
                  <div style={{ width: '100%', minHeight: 120, padding: '1rem', borderRadius: 12, border: '2px solid rgba(0,0,0,0.05)', background: '#f9f9f9', fontSize: '0.95rem', color: '#555', whiteSpace: 'pre-wrap' }}>
                    {studentNote || t('portal.exams.no_student_note', 'No student note yet.')}
                  </div>
                </div>
              </div>
            </>
          )}
        </ContentCard>

        <UnifiedModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          title={t('portal.exams.edit_title', 'Edit Exam') as string}
          variant="center"
          size="md"
        >
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 700, fontSize: '0.85rem' }}>{t('portal.exams.date_time', 'Date & Time')}</label>
              <input 
                type="datetime-local" 
                value={editData.startTime}
                onChange={e => setEditData({...editData, startTime: e.target.value})}
                style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 700, fontSize: '0.85rem' }}>{t('portal.exams.location', 'Location')}</label>
              <input 
                type="text" 
                value={editData.location}
                onChange={e => setEditData({...editData, location: e.target.value})}
                placeholder="e.g. Amsterdam Sloterdijk"
                style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 700, fontSize: '0.85rem' }}>{t('portal.register.student', 'Student')}</label>
              <select 
                value={editData.studentId}
                onChange={e => setEditData({...editData, studentId: e.target.value})}
                style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid #ddd' }}
              >
                <option value=""></option>
                {students.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 700, fontSize: '0.85rem' }}>{t('portal.exams.price', 'Price')}</label>
              <input 
                type="number" 
                step="0.01"
                value={editData.price}
                onChange={e => setEditData({...editData, price: e.target.value})}
                style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid #ddd' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 700, fontSize: '0.85rem' }}>{t('portal.exams.tips', 'Tips')}</label>
              <textarea 
                value={editData.tips}
                onChange={e => setEditData({...editData, tips: e.target.value})}
                style={{ width: '100%', padding: '0.6rem', borderRadius: 8, border: '1px solid #ddd', minHeight: '80px' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <ActionButton 
                onClick={() => setShowEdit(false)} 
                variant="danger" 
                style={{ flex: 1, background: '#eee', color: '#333' }}
              >
                {t('portal.common.cancel', 'Cancel')}
              </ActionButton>
              <ActionButton 
                onClick={handleUpdate} 
                disabled={saving}
                style={{ flex: 1, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}
              >
                {saving ? '…' : t('portal.common.save', 'Save')}
              </ActionButton>
            </div>
          </div>
        </UnifiedModal>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default TeacherExamDetail;
